// -*- coding: utf-8 -*-
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Assignment from '@mui/icons-material/Assignment';
import BarChart from '@mui/icons-material/BarChart';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import Chat from '@mui/icons-material/Chat';
import ClassIcon from '@mui/icons-material/Class';
import Person from '@mui/icons-material/Person';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MenuIcon from '@mui/icons-material/Menu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InsightsIcon from '@mui/icons-material/Insights';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import FactCheck from '@mui/icons-material/FactCheck';
import NotificationsActive from '@mui/icons-material/NotificationsActive';

import AdminLayout from './AdminLayout';
import AdminCard from './AdminCard';
import DashboardStats from './DashboardStats';
import TemplatesStatus from './messages/TemplatesStatus';
import AttendanceForm from './AttendanceForm';
import AttendanceStatus from './AttendanceStatus';
import NutritionForm from './NutritionForm';
import NutritionStatus from './NutritionStatus';
import MaterialForm from './MaterialForm';
import MaterialStatus from './MaterialStatus';
import TeacherHomepage from './TeacherHomepage.jsx';
import LearningOutcomeAssessmentPage from './maarif/LearningOutcomeAssessmentPage.jsx';
import LearningOutcomeAssessmentSummaryPage from './maarif/LearningOutcomeAssessmentSummaryPage.jsx';
import StudentMaarifPerformancePage from './maarif/StudentMaarifPerformancePage.jsx';
import ParentStudentManager from './parents/ParentStudentManager';
import ParentIntroductions from './parents/ParentIntroductions.jsx';
import MessagesPage from './messages/MessagesPage';
import IncomingMessagesLogPage from './messages/IncomingMessagesLogPage.jsx';
import ClassesManager from './classes/ClassesManager';
import TeachersManager from './teachers/TeachersManager';
import AdminsManager from './admins/AdminsManager';
import SchedulesManager from './schedules/SchedulesManager';
import TeacherSchedule from './schedules/TeacherSchedule';
import SchoolInfoManager from './school/SchoolInfoManager';
import LessonsManager from './lessons/LessonsManager';
import TeacherReminderSettings from './teachers/TeacherReminderSettings.jsx';
import ParentReminderSettings from './parents/ParentReminderSettings.jsx';
import SidebarNav, { buildGroups } from './SidebarNav';
import WeeklyReadingTrackingPage from './mini-tasks/WeeklyReadingTrackingPage';
import CetelesPage from './ceteles/CetelesPage';
import AdminPointsPage from './points/AdminPointsPage';
import LibraryScreenPage from './library/LibraryScreenPage';
import LibraryBookApprovalsPage from './library/LibraryBookApprovalsPage';
import HomeworksManager from './homeworks/HomeworksManager';
import HomeworkStatuses from './homeworks/HomeworkStatuses';
import PaymentsManager from './payments/PaymentsManager';
import PaymentsStatistics from './payments/PaymentsStatistics';
import DanduSubscriptionsManager from './payments/DanduSubscriptionsManager';
import BooksManager from './books/BooksManager';
import { PlaygroundContent } from './PlaygroundPanel';
import StudyLogsManager from './study-logs/StudyLogsManager.jsx';
import HomeworkAggregateSummary from './homeworks/HomeworkAggregateSummary.jsx';
import MockExamResultsPage from './exams/MockExamResultsPage.jsx';
import MockExamPlanningPage from './exams/MockExamPlanningPage.jsx';
import SchoolExamResultsPage from './exams/SchoolExamResultsPage.jsx';
import SchoolExamResultsRankingPage from './exams/SchoolExamResultsRankingPage.jsx';
import Student360DirectoryPage from './student360/Student360DirectoryPage.jsx';
import StudentActivitiesPage from './student360/StudentActivitiesPage.jsx';
import ValuesThemeManager from './values/ValuesThemeManager.jsx';
import ValuesScheduleManager from './values/ValuesScheduleManager.jsx';
import ValuesEvaluationResults from './values/ValuesEvaluationResults.jsx';
import ObservationJournalPage from './observation-journal/ObservationJournalPage.jsx';
import ObservationSummaryPage from './observation-journal/ObservationSummaryPage.jsx';
import CourseAssessmentManager from './exams/CourseAssessmentManager.jsx';
import CourseAssessmentTable from './exams/CourseAssessmentTable.jsx';
import ReadingSpeedTestManager from './exams/ReadingSpeedTestManager.jsx';
import ReadingSpeedTestTable from './exams/ReadingSpeedTestTable.jsx';
import StoryBookReadEvaluationManager from './exams/StoryBookReadEvaluationManager.jsx';
import StoryBookReadEvaluationTable from './exams/StoryBookReadEvaluationTable.jsx';
import { clearSchoolInfoCache, getSchoolInfoCached } from './api/schoolInfo';

function TabPanel(props) {
  const { children, value, index, shouldMount, ...other } = props;

  // Only render children if tab has been visited (shouldMount)
  const show = value === index;
  
  return (
    <div
      role="tabpanel"
      style={{ display: show ? 'block' : 'none' }}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {shouldMount && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TEACHER_HOME_TAB = 49;

const Dashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (['/admin/calisma-ozet', '/admin/calisma-katilim'].includes(location.pathname)) {
      navigate('/admin/calisma-gunlugu', { replace: true });
    }
  }, [location.pathname, navigate]);
  const isCeteleRoute = location.pathname === '/admin/cetelelerim';
  const isWeeklyReadingRoute = location.pathname === '/admin/mini-tasks/weekly-readings';
  const isPointsRoute = location.pathname === '/admin/points';
  const isStudentActivitiesRoute = location.pathname === '/admin/ogrenci-etkinlikleri';
  const isLibraryRoute = ['/admin/student-library', '/admin/ogrenci-kutuphanesi/ekran'].includes(location.pathname);
  const isBookApprovalsRoute = location.pathname === '/admin/ogrenci-kutuphanesi/kitap-onaylari';
  const isTeacher = user?.role === 'teacher';
  const isDemoSchool = String(user?.school_id || '').trim() === '1';
  const canTeacherAccessMockExamPlanning = isTeacher && isDemoSchool;
  const canAccessMockExamPlanning = !isTeacher || canTeacherAccessMockExamPlanning;
  const schoolId = user?.school_id || user?.school_name || '';
  const initialTab = isTeacher ? TEACHER_HOME_TAB : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [homeworkCreateRequest, setHomeworkCreateRequest] = useState(null);
  const [courseCreateRequest, setCourseCreateRequest] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [observationOpenTarget, setObservationOpenTarget] = useState(null);
  const observationOpenTargetSeq = useRef(0);
  
  // Track which tabs have been mounted at least once (use state to trigger re-renders)
  const [mountedTabs, setMountedTabs] = useState(new Set([initialTab]));
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [schoolSettings, setSchoolSettings] = useState({});

  const navigationGroups = useMemo(() => buildGroups(
    isTeacher,
    canTeacherAccessMockExamPlanning,
    schoolSettings,
    isTeacher ? 'teacher' : 'admin'
  ), [canTeacherAccessMockExamPlanning, isTeacher, schoolSettings]);

  const navigationItems = useMemo(
    () => navigationGroups.flatMap((group) => group.children),
    [navigationGroups]
  );

  const routeTabIndex = useMemo(
    () => navigationItems.find((item) => item.path === location.pathname)?.index,
    [location.pathname, navigationItems]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadSchoolSettings() {
      if (!schoolId) {
        if (!cancelled) {
          setSchoolSettings({});
        }
        return;
      }
      try {
        const schoolInfo = await getSchoolInfoCached(schoolId);
        if (!cancelled) {
          setSchoolSettings(schoolInfo?.settings || {});
        }
      } catch (_) {
        if (!cancelled) {
          setSchoolSettings({});
        }
      }
    }

    loadSchoolSettings();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const visibleTabIndexes = useMemo(() => {
    const indexes = new Set();
    navigationGroups.forEach((group) => {
      const groupItems = Array.isArray(group?.children)
        ? group.children
        : Array.isArray(group?.items)
          ? group.items
          : [];
      groupItems.forEach((item) => {
        if (!item.hidden && Number.isInteger(item.index) && item.index >= 0) {
          indexes.add(item.index);
        }
      });
    });
    return indexes;
  }, [navigationGroups]);

  useEffect(() => {
    if (!Number.isInteger(routeTabIndex) || routeTabIndex < 0 || !visibleTabIndexes.has(routeTabIndex)) {
      return;
    }
    setTabValue(routeTabIndex);
    setMountedTabs((prev) => {
      if (prev.has(routeTabIndex)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(routeTabIndex);
      return next;
    });
  }, [routeTabIndex, visibleTabIndexes]);

  useEffect(() => {
    if (!visibleTabIndexes.has(tabValue)) {
      const fallbackTab = visibleTabIndexes.has(initialTab)
        ? initialTab
        : Array.from(visibleTabIndexes).sort((left, right) => left - right)[0];
      if (typeof fallbackTab === 'number') {
        setTabValue(fallbackTab);
        setMountedTabs((prev) => {
          if (prev.has(fallbackTab)) {
            return prev;
          }
          const next = new Set(prev);
          next.add(fallbackTab);
          return next;
        });
      }
    }
  }, [initialTab, tabValue, visibleTabIndexes]);

  const handleSelect = React.useCallback((newValue) => {
    const selectedItem = navigationItems.find((item) => item.index === newValue && !item.hidden);
    if (selectedItem?.path && selectedItem.path !== location.pathname) {
      navigate(selectedItem.path);
    }

    // Add to mounted tabs when explicitly selecting
    setMountedTabs(prev => {
      if (!prev.has(newValue)) {
        const newSet = new Set(prev);
        newSet.add(newValue);
        return newSet;
      }
      return prev;
    });
    
    if (visibleTabIndexes.has(newValue)) {
      setTabValue(newValue);
    } else if (isTeacher) {
      setTabValue(TEACHER_HOME_TAB);
    } else {
      const fallbackTab = visibleTabIndexes.has(initialTab)
        ? initialTab
        : Array.from(visibleTabIndexes).sort((left, right) => left - right)[0];
      if (typeof fallbackTab === 'number') {
        setTabValue(fallbackTab);
      }
    }
    setError('');
    setSuccess('');
  }, [initialTab, isTeacher, location.pathname, navigate, navigationItems, visibleTabIndexes]);
  
  const handleSchoolInfoSaved = React.useCallback((nextInfo) => {
    clearSchoolInfoCache(schoolId);
    setSchoolSettings(nextInfo?.settings || {});
  }, [schoolId]);

  // Helper to determine if a tab should be mounted
  const shouldMountTab = (index) => mountedTabs.has(index);

  const handleAttendanceSuccess = React.useCallback((message) => {
    setSuccess(message);
    setError('');
  }, []);

  const handleError = React.useCallback((message) => {
    setError(message);
    setSuccess('');
  }, []);

  const handleOpenObservationJournal = React.useCallback((target) => {
    observationOpenTargetSeq.current += 1;
    setObservationOpenTarget({
      requestId: observationOpenTargetSeq.current,
      classId: String(target?.classId || ''),
      studentId: String(target?.studentId || ''),
    });
    handleSelect(30);
  }, [handleSelect]);

  return (
    <AdminLayout 
      user={user} 
      onLogout={onLogout}
      title="Okul Yönetici Paneli"
      onOpenMobileNav={() => setMobileOpen(true)}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ width: '100%', display: 'flex' }}>
        {/* Sidebar */}
        <SidebarNav
          activeIndex={tabValue}
          onSelect={handleSelect}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          isTeacher={isTeacher}
          showTeacherDenemePlanlama={canTeacherAccessMockExamPlanning}
          featureSettings={schoolSettings}
        />

        {/* Main content area */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 0, md: 2 } }}>
          {/* Mobile menu button */}
          <Box sx={{ display: { xs: isCeteleRoute ? 'none' : 'flex', md: 'none' }, alignItems: 'center', px: 1, pt: 1 }}>
            <Box sx={{ flex: 1 }} />
            <Box>
              <MenuIcon onClick={() => setMobileOpen(true)} style={{ cursor: 'pointer' }} />
            </Box>
          </Box>

          {isStudentActivitiesRoute ? (
            <StudentActivitiesPage schoolId={schoolId} />
          ) : isCeteleRoute ? (
            <CetelesPage schoolId={schoolId} />
          ) : isWeeklyReadingRoute ? (
            <WeeklyReadingTrackingPage />
          ) : isPointsRoute ? (
            <AdminPointsPage schoolId={schoolId} canAward={isTeacher} awardActorId={user?.user_id || user?.phone || user?.id || user?.sub} />
          ) : isLibraryRoute ? (
            <LibraryScreenPage />
          ) : isBookApprovalsRoute ? (
            <LibraryBookApprovalsPage />
          ) : (
          <>

          {/* General Status Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={0} shouldMount={shouldMountTab(0)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Okul İstatistikleri" 
                  subtitle="Genel okul durumu ve özetler"
                  icon={DashboardIcon}
                >
                  <DashboardStats onError={handleError} schoolId={schoolId} />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          <TabPanel value={tabValue} index={TEACHER_HOME_TAB} shouldMount={shouldMountTab(TEACHER_HOME_TAB)}>
            <TeacherHomepage
              schoolId={schoolId}
              showLibraryApprovals={!navigationItems.find((item) => item.key === 'kutuphanem')?.hidden}
              onError={handleError}
              onSelectTab={handleSelect}
              onOpenHomework={(classId) => {
                setHomeworkCreateRequest({ id: Date.now(), schoolId, classId: String(classId || '') });
                handleSelect(12);
              }}
              onOpenObservationJournal={handleOpenObservationJournal}
            />
          </TabPanel>

          {/* Attendance Management Tab */}
          <TabPanel value={tabValue} index={1} shouldMount={shouldMountTab(1)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Devamsızlık Kaydı" 
                  subtitle="Öğrenci devamsızlık durumlarını kaydedin"
                  icon={Assignment}
                >
                  <AttendanceForm 
                    onSuccess={handleAttendanceSuccess} 
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Attendance Status Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={2} shouldMount={shouldMountTab(2)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Devamsızlık Durumu" 
                  subtitle="Öğrenci devamsızlık durumlarını görüntüleyin ve analiz edin"
                  icon={BarChart}
                >
                  <AttendanceStatus 
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Nutrition Management Tab */}
          <TabPanel value={tabValue} index={39} shouldMount={shouldMountTab(39)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Günlük Beslenme Değerlendirme"
                  subtitle="Öğrenci beslenme durumlarını kaydedin"
                  icon={FactCheck}
                >
                  <NutritionForm
                    onSuccess={handleAttendanceSuccess}
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Student Material Management Tab */}
          <TabPanel value={tabValue} index={47} shouldMount={shouldMountTab(47)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğrenci Materyalleri"
                  subtitle="Öğrenci materyal durumlarını kaydedin"
                  icon={FactCheck}
                >
                  <MaterialForm
                    onSuccess={handleAttendanceSuccess}
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Learning Outcome Assessment (Yeni Maarif Model) */}
          <TabPanel value={tabValue} index={41} shouldMount={shouldMountTab(41)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğrenme Çıktısı Değerlendirme"
                  subtitle="Yeni Maarif Model'e göre öğrenme çıktısı bazlı değerlendirme"
                  icon={FactCheck}
                >
                  <LearningOutcomeAssessmentPage
                    onSuccess={handleAttendanceSuccess}
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Learning Outcome Assessment Summary (Yeni Maarif Model) */}
          <TabPanel value={tabValue} index={42} shouldMount={shouldMountTab(42)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğrenme Çıktısı Özeti"
                  subtitle="Şube bazlı, salt okunur değerlendirme özeti"
                  icon={InsightsIcon}
                >
                  <LearningOutcomeAssessmentSummaryPage
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Student Performance (Yeni Maarif Model) */}
          <TabPanel value={tabValue} index={43} shouldMount={shouldMountTab(43)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğrenci Performansı"
                  subtitle="Seçilen öğrenci için değerlendirme özeti"
                  icon={InsightsIcon}
                >
                  <StudentMaarifPerformancePage
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Nutrition Status Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={40} shouldMount={shouldMountTab(40)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Beslenme Durumu"
                  subtitle="Beslenme kayıtlarını görüntüleyin"
                  icon={BarChart}
                >
                  <NutritionStatus
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Student Material Status Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={48} shouldMount={shouldMountTab(48)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Materyal Durumu"
                  subtitle="Materyal kayıtlarını görüntüleyin"
                  icon={BarChart}
                >
                  <MaterialStatus
                    onError={handleError}
                    schoolId={schoolId}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Parent-Student Management Tab */}
          {!isTeacher && <TabPanel value={tabValue} index={52} shouldMount={shouldMountTab(52)}>
            <ParentIntroductions schoolId={schoolId} />
          </TabPanel>}
          <TabPanel value={tabValue} index={3} shouldMount={shouldMountTab(3)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Veli-Öğrenci Yönetimi" 
                  subtitle="Veliler ve öğrenciler için kayıt ve ilişkilendirme işlemleri"
                  icon={PeopleAlt}
                  sx={{ '&:hover': { transform: 'none' } }}
                >
                  <ParentStudentManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Classes Tab */}
          <TabPanel value={tabValue} index={4} shouldMount={shouldMountTab(4)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title={isTeacher ? 'Atandığım Şubeler' : 'Şube Yönetimi'}
                  subtitle={isTeacher ? 'Yalnızca size atanmış şubeleri görüntülüyorsunuz' : 'Şube oluşturma, düzenleme ve silme'}
                  icon={ClassIcon}
                >
                  <ClassesManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    readOnly={isTeacher}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Lessons Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={5} shouldMount={shouldMountTab(5)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Ders Yönetimi" 
                  subtitle="Ders listesi ve düzenleme"
                  icon={MenuBookIcon}
                >
                  <LessonsManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Values Themes Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={27} shouldMount={shouldMountTab(27)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Değer Temaları" 
                  subtitle="Yeni tema ekleyin, düzenleyin veya silin"
                  icon={ColorLensIcon}
                >
                  <ValuesThemeManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          {/* Values Programs (schedule) */}
          <TabPanel value={tabValue} index={28} shouldMount={shouldMountTab(28)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Değer Programı" 
                  subtitle="Tema bazlı gönderim takvimini sınıf için planlayın"
                  icon={ScheduleSendIcon}
                >
                  <ValuesScheduleManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Values Evaluation Results */}
          <TabPanel value={tabValue} index={29} shouldMount={shouldMountTab(29)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Değerlendirme Sonuçları" 
                  subtitle="Tema bazlı gözlem formu yanıtlarını görüntüleyin"
                  icon={FactCheck}
                >
                  <ValuesEvaluationResults
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Schedules Tab */}
          <TabPanel value={tabValue} index={6} shouldMount={shouldMountTab(6)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Ders Programı" 
                  subtitle="Şubeler için haftalık ders programını yönetin"
                  icon={ScheduleIcon}
                >
                  <SchedulesManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Teacher Schedules Tab: visible to both admin and teacher */}
          <TabPanel value={tabValue} index={11} shouldMount={shouldMountTab(11)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğretmen Ders Programı"
                  subtitle="Öğretmene göre haftalık ders programını görüntüleyin"
                  icon={ScheduleIcon}
                >
                  <TeacherSchedule
                    schoolId={schoolId}
                    onError={handleError}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Homeworks Tab: visible to both admin and teacher (teacher can edit with token) */}
          <TabPanel value={tabValue} index={12} shouldMount={shouldMountTab(12)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ödev Yönetimi"
                  subtitle="Şubelere ödev ekleyin, düzenleyin ve silin"
                  icon={Assignment}
                >
                  <HomeworksManager
                    createRequest={homeworkCreateRequest}
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Ödev Durumları: sınıf → ödev → öğrenci durumları */}
          <TabPanel value={tabValue} index={20} shouldMount={shouldMountTab(20)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ödev Durumları"
                  subtitle="Şube ve ödev seçip öğrenci durumlarını işaretleyin"
                  icon={Assignment}
                >
                  <HomeworkStatuses
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Ödev Durumu Özeti Tab */}
          <TabPanel value={tabValue} index={23} shouldMount={shouldMountTab(23)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ödev Durumu Özeti"
                  subtitle="Yapılan, eksik ve geç ödev sayıları; tamamlanma yüzdesi"
                  icon={Assignment}
                >
                  <HomeworkAggregateSummary
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={50} shouldMount={shouldMountTab(50)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Öğrenci360"
                  subtitle="Öğrencileri listeleyin ve tek tıkla öğrenci360 sayfasını açın"
                  icon={PeopleAlt}
                >
                  <Student360DirectoryPage
                    schoolId={schoolId}
                    onError={handleError}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
{/* Payment Plan Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={51} shouldMount={shouldMountTab(51)}>
            <DanduSubscriptionsManager schoolId={schoolId} />
          </TabPanel>
          )}
          {!isTeacher && (
          <TabPanel value={tabValue} index={13} shouldMount={shouldMountTab(13)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ödeme Planı"
                  subtitle="Öğrenci ödeme planı ve taksit yönetimi"
                  icon={ReceiptLongIcon}
                >
                  <PaymentsManager
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          {/* Payment Statistics Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={14} shouldMount={shouldMountTab(14)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ödeme İstatistikleri"
                  subtitle="Okul genelindeki ödeme performansı"
                  icon={InsightsIcon}
                >
                  <PaymentsStatistics
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          {!isTeacher && (
          <TabPanel value={tabValue} index={16} shouldMount={shouldMountTab(16)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Şablon Durumu" 
                  subtitle="WhatsApp mesaj şablonlarının yüklenme ve anahtar durumu"
                  icon={Chat}
                >
                  <TemplatesStatus />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {!isTeacher && (
          <TabPanel value={tabValue} index={7} shouldMount={shouldMountTab(7)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="WhatsApp Mesajları" 
                  subtitle="Velilerle mesajlaşma geçmişi ve yanıt gönderme"
                  icon={Chat}
                >
                  <MessagesPage 
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          {!isTeacher && (
          <TabPanel value={tabValue} index={45} shouldMount={shouldMountTab(45)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Gelen Mesajlar"
                  subtitle="Velilerden gelen son 50 WhatsApp mesaji gunlugu"
                  icon={Chat}
                >
                  <IncomingMessagesLogPage
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          {/* Teachers Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={8} shouldMount={shouldMountTab(8)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Öğretmen Yönetimi" 
                  subtitle="Öğretmen oluşturma, düzenleme ve silme"
                  icon={Person}
                >
                  <TeachersManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Teacher Reminders Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={26} shouldMount={shouldMountTab(26)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Öğretmen Hatırlatmaları" 
                  subtitle="Hafta içi 17:30’ta yoklama ve ödev hatırlatmaları gönderilir"
                  icon={Person}
                >
                  <TeacherReminderSettings
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Parent Reminders Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={38} shouldMount={shouldMountTab(38)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Veli Hatırlatmaları"
                  subtitle="Seçtiğiniz şube/sınıf seviyesine otomatik hatırlatma gönderilir"
                  icon={NotificationsActive}
                >
                  <ParentReminderSettings
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Admins Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={9} shouldMount={shouldMountTab(9)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Yöneticiler" 
                  subtitle="Yönetici ekleme, düzenleme ve silme"
                  icon={AdminPanelSettings}
                >
                  <AdminsManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* School Info Tab */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={10} shouldMount={shouldMountTab(10)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard 
                  title="Okul Bilgileri" 
                  subtitle="Okul ayarları ve WhatsApp numarası"
                  icon={SchoolIcon}
                >
                  <SchoolInfoManager 
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    onSaved={handleSchoolInfoSaved}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Books Tab: visible to both admin and teacher (teacher can edit/delete only own) */}
          <TabPanel value={tabValue} index={15} shouldMount={shouldMountTab(15)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Kitap Önerileri"
                  subtitle="Sınıf seviyesine göre yardımcı ve okuma kitapları"
                  icon={MenuBookIcon}
                >
                  <BooksManager
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={30} shouldMount={shouldMountTab(30)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Gözlem Günlüğü"
                  subtitle="Öğrenciler için gözlem notları ekleyin ve velilerle paylaşın"
                  icon={FactCheck}
                >
                  <ObservationJournalPage
                    schoolId={schoolId}
                    user={user}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    openTarget={observationOpenTarget}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tabValue} index={31} shouldMount={shouldMountTab(31)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Gözlem Özeti"
                  subtitle="Şube bazında toplam ve gönderilen gözlem sayılarını takip edin"
                  icon={InsightsIcon}
                >
                  <ObservationSummaryPage
                    schoolId={schoolId}
                    user={user}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Playground Tab (embedded simulation) */}
          {!isTeacher && (
          <TabPanel value={tabValue} index={17} shouldMount={shouldMountTab(17)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Playground"
                  subtitle="Mesaj simülasyonu ve overlay izleme"
                  icon={Chat}
                >
                  <PlaygroundContent embedded />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}

          {/* Study Logs Tab */}
          <TabPanel value={tabValue} index={18} shouldMount={shouldMountTab(18)}>
            <StudyLogsManager
              schoolId={schoolId}
              onError={handleError}
              onSuccess={handleAttendanceSuccess}
            />
          </TabPanel>

          {/* Exam Results Tab (visible to both admin and teacher) */}
          <TabPanel value={tabValue} index={19} shouldMount={shouldMountTab(19)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Sınav Sonuçları"
                  subtitle="Şube ve ada göre filtreleyin; en yüksek netler üstte"
                  icon={InsightsIcon}
                >
                  <MockExamResultsPage
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Course assessment manager */}
          <TabPanel value={tabValue} index={32} shouldMount={shouldMountTab(32)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                  <CourseAssessmentManager
                    createRequestId={courseCreateRequest}
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
              </Grid>
            </Grid>
          </TabPanel>
          {/* Course assessment aggregated table */}
          <TabPanel value={tabValue} index={33} shouldMount={shouldMountTab(33)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ders Değerlendirmeleri"
                  subtitle="Sınıfın genel görünümünü, ders ortalamalarını ve öğrenci gelişimini keşfedin"
                  icon={BarChart}
                >
                  <CourseAssessmentTable
                    schoolId={schoolId}
                    onError={handleError}
                    onCreateAssessment={() => { setCourseCreateRequest((current) => current + 1); handleSelect(32); }}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Reading Exam Manager */}
          <TabPanel value={tabValue} index={34} shouldMount={shouldMountTab(34)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Kitap Okuma Sınavları"
                  subtitle={(
                    <Box>
                      <Box sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                        Değerlendirmeyi oluşturun, puanları girin ve sonuçları velilerle paylaşın.
                      </Box>
                      <Box sx={{ fontSize: '0.85rem', lineHeight: 1.5, mt: 0.5, color: 'text.secondary' }}>
                        60 ve üzeri puan alan öğrencilerde kitap otomatik olarak tamamlananlara eklenir.
                      </Box>
                    </Box>
                  )}
                  icon={InsightsIcon}
                >
                  <StoryBookReadEvaluationManager
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Reading Exam Aggregated Table */}
          <TabPanel value={tabValue} index={35} shouldMount={shouldMountTab(35)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Kitap Okuma Tablosu"
                  subtitle="Şubelere göre hikaye sınavı sonuçlarını takip edin"
                  icon={BarChart}
                >
                  <StoryBookReadEvaluationTable
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Reading Speed Test Manager */}
          <TabPanel value={tabValue} index={36} shouldMount={shouldMountTab(36)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Manuel Okuma Hızı Testleri"
                  subtitle="Test oluşturun, sözcük sayılarını kaydedin ve velilerle paylaşın"
                  icon={InsightsIcon}
                >
                  <ReadingSpeedTestManager
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Reading Speed Test Table */}
          <TabPanel value={tabValue} index={37} shouldMount={shouldMountTab(37)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Okuma Hızı Tablosu"
                  subtitle="Şube bazında okuma hızı sonuçlarını filtreleyin"
                  icon={BarChart}
                >
                  <ReadingSpeedTestTable
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* School Exam Results Tab (regular school exams) */}
          <TabPanel value={tabValue} index={24} shouldMount={shouldMountTab(24)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Okul Sınav Sonuçları"
                  subtitle="Şube ve derse göre düzenleyin; dönem ortalamaları"
                  icon={InsightsIcon}
                >
                  <SchoolExamResultsPage
                    schoolId={schoolId}
                    onError={handleError}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* School Exam Results Ranking Tab */}
          <TabPanel value={tabValue} index={25} shouldMount={shouldMountTab(25)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Ortalama Sıralaması"
                  subtitle="Öğrenciler ders ortalamalarının genel ortalaması ile sıralanır"
                  icon={InsightsIcon}
                >
                  <SchoolExamResultsRankingPage
                    schoolId={schoolId}
                    onError={handleError}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          {/* Exam Planning Tab (admin + demo-school teacher) */}
          {canAccessMockExamPlanning && (
          <TabPanel value={tabValue} index={21} shouldMount={shouldMountTab(21)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AdminCard
                  title="Deneme Planlama ve İçe Aktarma"
                  subtitle="Plan oluşturun, sonuç dosyası yükleyin, eşleştirin ve gönderin"
                  icon={InsightsIcon}
                >
                  <MockExamPlanningPage
                    schoolId={schoolId}
                    onError={handleError}
                    onSuccess={handleAttendanceSuccess}
                    user={user}
                  />
                </AdminCard>
              </Grid>
            </Grid>
          </TabPanel>
          )}
          </>
          )}
        </Box>
      </Box>
    </AdminLayout>
  );
};

export default Dashboard;
